import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import api from '../services/api';

function Account() {
  const [profileData, setProfileData] = useState(null);
  const [form, setForm] = useState({ firstName: '', lastName: '', company: '', password: '', profilePic: null });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, isAuthenticated, refreshProfile } = useSupabaseAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');

    if (sessionId) {
      // Flow 1: Verify Session (Verification Mode)
      const verifyUpgrade = async () => {
        try {
          const res = await api.post('/api/payments/verify-session', { sessionId });
          const data = res.data;
          if (data.success) {
            toast.success('Subscription upgraded successfully!');
            setProfileData(data.user);
            await refreshProfile();
            navigate('/account', { replace: true });
          } else {
            toast.error(data.error || data.message || 'Verification failed. Please contact support.');
            navigate('/account', { replace: true });
          }
        } catch (e) {
          console.error('Verification failed', e);
          toast.error(`Verification failed: ${e.message}`);
          navigate('/account', { replace: true });
        }
      };
      verifyUpgrade();
    } else {
      // Flow 2: Load Profile (Normal Mode)
      (async () => {
        try {
          const response = await api.get('/api/auth/me');
          const data = response.data;
          setProfileData(data);
          setForm({
            firstName: data.first_name || data.firstName || '',
            lastName: data.last_name || data.lastName || '',
            company: data.company || '',
            password: '',
            profilePic: null,
          });
        } catch (err) {
          if (!isAuthenticated) {
            toast.error('Please log in.');
            navigate('/login');
          }
        }
      })();
    }
  }, [navigate, location.search, isAuthenticated, refreshProfile]);

  const handleUpgrade = async () => {
    try {
      if (!isAuthenticated || !authUser) {
        toast.error('Please log in to upgrade.');
        return navigate('/login');
      }

      toast.info('Redirecting to secure checkout...');

      const res = await api.post('/api/payments/create-checkout-session', {
        priceId: null,
        plan: 'growth',
        userId: authUser.id,
        customerEmail: authUser.email
      });
      const data = res.data;
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not initiate checkout.');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to initiate upgrade');
    }
  }

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.put('/api/auth/me', form);
      setProfileData(response.data);
      await refreshProfile();
      toast.success('Profile updated!');
      setForm((prev) => ({ ...prev, password: '' }));
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const profile = profileData;

  if (!profile) return <div className="py-10 text-center">Loading profile...</div>;

  return (
    <div className="max-w-xl mx-auto bg-white p-8 rounded shadow">
      <h2 className="text-2xl font-bold mb-6">Account Settings</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex flex-col items-center mb-4">
          <img
            src={profile.profilePic || '/default-avatar.png'}
            alt="Profile"
            className="w-24 h-24 rounded-full object-cover mb-2"
          />
          <input type="file" name="profilePic" accept="image/*" onChange={handleChange} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">First Name</label>
          <input
            type="text"
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Last Name</label>
          <input
            type="text"
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input
            type="email"
            value={profile.email}
            disabled
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 bg-gray-100 cursor-not-allowed"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Company</label>
          <input
            type="text"
            name="company"
            value={form.company}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">New Password</label>
          <input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2"
            placeholder="Leave blank to keep current password"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-indigo-600 text-white py-2 px-4 rounded hover:bg-indigo-700 font-semibold"
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </form>

      {/* Subscription Section */}
      <div className="mt-12 border-t pt-8">
        <h3 className="text-xl font-bold mb-4">Subscription Plan</h3>
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-500 uppercase tracking-wide font-semibold">Current Plan</p>
              <p className="text-3xl font-bold text-indigo-600 mt-1">
                {profile.subscription?.plan?.toUpperCase() || 'FREE'}
              </p>
              {profile.subscription?.status && (
                <p className="text-sm text-gray-600 mt-1 capitalize">
                  Status: <span className={profile.subscription.status === 'active' ? 'text-green-600 font-medium' : 'text-red-500'}>
                    {profile.subscription.status.replace('_', ' ')}
                  </span>
                </p>
              )}
              {profile.subscription?.currentPeriodEnd && (
                <p className="text-xs text-gray-500 mt-2">
                  {profile.subscription.status === 'active' ? 'Renews' : 'Expires'} on {new Date(profile.subscription.currentPeriodEnd).toLocaleDateString()}
                </p>
              )}
            </div>

            <div>
              {profile.subscription?.plan === 'growth' || profile.subscription?.plan === 'enterprise' ? (
                <button
                  onClick={async () => {
                    if (!window.confirm('Are you sure you want to cancel? Your access will continue until the end of the billing period.')) return;
                    try {
                      const res = await api.post('/api/payments/cancel-subscription');
                      const data = res.data;
                      toast.info(`Subscription cancelled. Active until ${new Date(data.currentPeriodEnd).toLocaleDateString()}`);
                      const updatedRes = await api.get('/api/auth/me');
                      setProfileData(updatedRes.data);
                      await refreshProfile();
                    } catch (e) {
                      console.error(e);
                      toast.error(e.response?.data?.message || 'Error processing cancellation');
                    }
                  }}
                  className="bg-white text-red-600 border border-red-200 px-4 py-2 rounded hover:bg-red-50 transition-colors"
                >
                  Cancel Subscription
                </button>
              ) : (
                <button
                  onClick={handleUpgrade}
                  className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  Upgrade to Growth
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Account; 